import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Play, ListChecks, ScrollText, Utensils, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function DashboardPage() {
  const [stats, setStats] = useState({ sources: 0, specials: 0, lastRun: null });
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [s, sp, lg] = await Promise.all([
        api.get("/admin/sources"),
        api.get("/specials"),
        api.get("/admin/logs"),
      ]);
      const lastLog = lg.data[0];
      setStats({
        sources: s.data.length,
        specials: sp.data.length,
        lastRun: lastLog ? `${new Date(lastLog.ran_at).toLocaleString()} · ${lastLog.status}` : "Never",
      });
    } catch {
      toast.error("Failed to load stats");
    }
  };

  useEffect(() => { load(); }, []);

  const runAll = async () => {
    setRunning(true);
    try {
      await api.post("/admin/extract-all");
      toast.success("Extraction queued for all sources");
      setTimeout(load, 4000);
    } catch {
      toast.error("Failed to trigger extraction");
    }
    setRunning(false);
  };

  return (
    <div className="px-4 py-5 sm:p-8 max-w-6xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-[#6A6F5C] text-sm mt-1">Overview of extraction system.</p>
        </div>
        <Button onClick={runAll} disabled={running} className="w-full bg-[#E07A5F] hover:bg-[#cf6b51] sm:w-auto" data-testid="run-all-btn">
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play size={14} className="mr-2" />}
          Run extraction on all sources
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[rgba(61,64,53,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.18em] text-[#6A6F5C] font-semibold flex items-center gap-2">
              <ListChecks size={14} /> Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-heading text-4xl font-bold" data-testid="stat-sources">{stats.sources}</div>
          </CardContent>
        </Card>
        <Card className="border-[rgba(61,64,53,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.18em] text-[#6A6F5C] font-semibold flex items-center gap-2">
              <Utensils size={14} /> Specials in database
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-heading text-4xl font-bold" data-testid="stat-specials">{stats.specials}</div>
          </CardContent>
        </Card>
        <Card className="border-[rgba(61,64,53,0.08)] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.18em] text-[#6A6F5C] font-semibold flex items-center gap-2">
              <ScrollText size={14} /> Last extraction
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-heading text-base font-medium" data-testid="stat-last-run">{stats.lastRun}</div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 bg-white border border-[rgba(61,64,53,0.08)] rounded-xl p-4 sm:mt-10 sm:p-6">
        <h2 className="font-heading text-xl font-semibold mb-3">How it works</h2>
        <ol className="list-decimal pl-5 text-sm text-[#3D4035] space-y-2 leading-relaxed">
          <li>Add a source (HTML page, PDF, image, or Instagram profile) under <strong>Sources</strong>.</li>
          <li>Set the restaurant's lat/lng so users can find it nearby.</li>
          <li>Hit <em>Extract</em> on a row, or <em>Run extraction on all sources</em> above.</li>
          <li>For Instagram, configure your session credentials under <strong>Instagram</strong> first.</li>
          <li>The scheduler also runs a full pass daily at 09:00 Europe/Berlin.</li>
        </ol>
      </div>
    </div>
  );
}
